<script lang="ts">
    import { DEFAULT_LAYOUT, ANIMATION_SPEED } from '$lib/components/constants';

    import { browser } from '$app/environment';
    import { cn } from '$lib/utils/cn';

    import { onMount, tick } from 'svelte';

    import LogosList from './LogosList.svelte';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import Box from '../_wrappers/Box.svelte';

    import type { LogoSize } from '$lib/types/Components/Logos/LogosContent';
    import type { Box as BoxType } from '$lib/types/Components/SharedTypes';
    import type { TransitionEffect } from '$lib/types/Theme';

    export let id = '';
    export let box: BoxType;
    export let images: string[] = [];
    export let isGrayscale = false;
    export let size: LogoSize = 'medium';
    export let logoGapXPx: number | undefined = undefined;
    export let paddingClass: string | undefined = undefined;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let layout = DEFAULT_LAYOUT;

    const isInColumn = layout.isInColumn;

    let containerEl: HTMLDivElement | undefined;
    let measureEl: HTMLDivElement | undefined;

    let hasAnimation = false;
    let contentWidth = 0;

    // The logo slider should scroll whenever a single set of logos is wider than the
    // visible container, i.e. the logos don't fit. We measure this from the rendered
    // DOM so the decision stays correct for every logo size, column layout and embed
    // width, instead of relying on a logo-count/column-count heuristic that ignored
    // the logo size (FUN-3783).
    const measure = () => {
        if (!containerEl || !measureEl) return;

        contentWidth = measureEl.scrollWidth;
        hasAnimation = contentWidth > containerEl.clientWidth;
    };

    $: animationDuration = hasAnimation ? images.length / ANIMATION_SPEED : 0;

    // Re-measure whenever the rendered logo set can change.
    $: measureKey = `${images.length}-${size}-${logoGapXPx}`;
    $: if (browser && measureKey) tick().then(measure);

    onMount(() => {
        measure();

        const observer = new ResizeObserver(() => measure());

        if (containerEl) {
            observer.observe(containerEl);
        }

        return () => observer.disconnect();
    });
</script>

<Box {id} {...getBoxAttributes({ box, isInColumn })} {transitionDelay} {transitionEffect}>
    <div
        bind:this={containerEl}
        class={cn(
            'fun-relative fun-w-full fun-overflow-hidden fun-contain-inline-size',
            paddingClass,
        )}
        style:mask-image={hasAnimation
            ? 'linear-gradient(90deg, transparent 5%, rgba(0, 0, 0, 1) 20%, rgba(0, 0, 0, 1) 80%, transparent 95%)'
            : undefined}
    >
        <div
            class={cn('fun-flex fun-items-center fun-justify-center', {
                'fun-animate-logos-slide-loop': hasAnimation,
                'fun-opacity-75': isGrayscale,
            })}
            style:width={hasAnimation ? `${contentWidth * 2}px` : undefined}
            style:animation-duration={hasAnimation ? `${animationDuration}s` : undefined}
        >
            <div bind:this={measureEl} class="fun-flex fun-items-center">
                <LogosList {images} {isGrayscale} {size} {logoGapXPx} />
            </div>

            {#if hasAnimation}
                <div class="fun-flex fun-items-center" aria-hidden="true">
                    <LogosList {images} {isGrayscale} {size} {logoGapXPx} />
                </div>
            {/if}
        </div>
    </div>
</Box>
